import { Alerta, Badge, Button, DataList, Desplegable, Dialog, Row, Spinner, Stack, type ColumnaDataList } from "@/components";
import type { MovimientoHistorial } from "@/lib/api";
import { etiquetaAccion, formatCuando, formatValorCambio, tonoAccion } from "@/lib/historial";
import { useHistorialTab } from "./useHistorialTab";

function Cambios({ m }: { m: MovimientoHistorial }) {
  if (m.cambios.length === 0) return null;
  return (
    <Desplegable titulo={m.cambios.length === 1 ? "Ver el cambio" : `Ver los ${m.cambios.length} cambios`}>
      <ul aria-label={`Cambios del movimiento ${m.id}`} className="grid gap-1 text-chico">
        {m.cambios.map((c) => (
          <li key={c.campo}>
            <span className="font-medium">{c.campo}:</span> <span className="text-texto-suave line-through">{formatValorCambio(c.antes)}</span> →{" "}
            <span>{formatValorCambio(c.despues)}</span>
          </li>
        ))}
      </ul>
    </Desplegable>
  );
}

export function HistorialTab() {
  const t = useHistorialTab();

  const columnas: ColumnaDataList<MovimientoHistorial>[] = [
    { clave: "cuando", titulo: "Cuándo", render: (m) => <span className="tabular-nums whitespace-nowrap">{formatCuando(m.creado_en)}</span> },
    { clave: "accion", titulo: "Tipo", render: (m) => <Badge tono={tonoAccion(m)}>{etiquetaAccion(m)}</Badge> },
    {
      clave: "que",
      titulo: "Qué pasó",
      render: (m) => (
        <Stack espacio={1}>
          <span className={m.deshecho_por ? "text-texto-suave line-through" : undefined}>{m.titulo}</span>
          {m.deshecho_por && (
            <span className="text-chico text-alerta">
              Deshecho por {m.deshecho_por.usuario} el {formatCuando(m.deshecho_por.creado_en)}
            </span>
          )}
          <Cambios m={m} />
        </Stack>
      ),
    },
  ];

  const acciones = (m: MovimientoHistorial) =>
    t.esAdmin && m.se_puede_deshacer ? (
      <Button variante="secundario" tamanio="sm" aria-label={`Deshacer: ${m.titulo}`} onClick={() => t.pedirDeshacer(m)}>
        Deshacer
      </Button>
    ) : null;

  return (
    <Stack espacio={6}>
      <Stack espacio={1}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Historial</h1>
        <p className="text-chico text-texto-suave">
          Cada cambio del desarrollo y quién lo hizo, del más nuevo al más viejo. Deshacer no borra nada del historial: queda anotado quién lo deshizo y cuándo.
        </p>
      </Stack>

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}
      {!t.cargando && !t.errorCarga && (
        <>
          <DataList etiqueta="Historial" columnas={columnas} filas={t.items} claveFila={(m) => m.id} acciones={acciones} vacio="Todavía no hay movimientos." />
          <Alerta texto={t.errorMas} />
          {t.hayMas && (
            <div>
              <Button variante="secundario" cargando={t.cargandoMas} onClick={t.verMas}>
                Ver más
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog abierto={t.aDeshacer !== null} onCambio={(abierto) => !abierto && t.cancelarDeshacer()} titulo="Deshacer movimiento">
        <Stack espacio={4}>
          <p>{t.aDeshacer?.titulo}</p>
          <p className="text-chico text-texto-suave">
            {t.aDeshacer?.accion === "crear"
              ? "Se borra lo que se cargó (con sus comprobantes adjuntos)."
              : t.aDeshacer?.accion === "editar"
                ? "Vuelve a como estaba antes de esta edición."
                : "Se vuelve a cargar con los mismos datos (los comprobantes adjuntos no vuelven)."}{" "}
            Las dos cosas quedan en el historial.
          </p>
          <Alerta texto={t.errorDeshacer} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cancelarDeshacer}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.deshaciendo} onClick={t.confirmarDeshacer}>
              Deshacer
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
